import { clamp } from 'es-toolkit';
import { useLayoutEffect, useRef } from 'preact/hooks';
import { classNames } from '../utils/helpers';

// Type definitions
interface Button<T extends string> {
  id: T;
  children: preact.ComponentChildren;
}

interface ButtonBarProps<T extends string> {
  buttons: readonly Button<T>[];
  selectedId: NoInfer<T | null>;
  onSelect: (id: NoInfer<T>) => void;
  buttonClassName?: string | ((id: NoInfer<T>) => string);
  wrapButton?: (
    id: NoInfer<T>,
    children: preact.ComponentChildren
  ) => preact.JSX.Element;
  easing?: string;
}

type SpanRefs<T extends string> = Record<T, HTMLSpanElement | null>;

// ButtonBar component - handles rendering and internal selection state
export const ButtonBar = <T extends string>({
  buttons,
  selectedId,
  onSelect,
  buttonClassName,
  wrapButton,
  easing,
}: ButtonBarProps<T>) => {
  const spanRefs: SpanRefs<T> = {} as SpanRefs<T>; // Add refs on render
  let lastSelectedIdRef = useRef<T>(selectedId);

  // When selectedId changes, do FLIP animation
  useLayoutEffect(() => {
    const lastId = lastSelectedIdRef.current;
    if (lastId && selectedId && lastId !== selectedId) {
      const startSpan = spanRefs[lastId];
      const endSpan = spanRefs[selectedId];
      if (startSpan && endSpan) {
        flipAnimation(startSpan, endSpan, easing);
      } else {
        // Should never happen
        console.warn('Missing span refs for FLIP animation', {
          before: [lastId, startSpan],
          after: [selectedId, endSpan],
        });
      }
    }
    lastSelectedIdRef.current = selectedId;
  }, [selectedId]);

  return (
    <>
      {buttons.map(({ id, children }) => {
        const button = (
          <button
            key={id}
            onClick={() => {
              onSelect(id);
            }}
            class={classNames(
              'relative m-auto flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium',
              typeof buttonClassName === 'function'
                ? buttonClassName(id)
                : buttonClassName
            )}
          >
            <span
              class={classNames(
                'absolute left-0 top-0 -z-[1] h-full w-full transition-colors',
                id === selectedId ? 'bg-slate-200' : ''
              )}
              ref={(elem: HTMLSpanElement | null) => {
                spanRefs[id] = elem;
              }}
            ></span>
            {children}
          </button>
        );
        return wrapButton ? wrapButton(id, button) : button;
      })}
    </>
  );
};

// Do FLIP animation
function flipAnimation(
  startSpan: HTMLSpanElement,
  endSpan: HTMLSpanElement,
  easing = 'ease-out'
) {
  const startRect = startSpan.getBoundingClientRect();
  const endRect = endSpan.getBoundingClientRect();

  const deltaX = startRect.left - endRect.left;
  const deltaY = startRect.top - endRect.top;
  const scaleX = startRect.width / endRect.width;
  const scaleY = startRect.height / endRect.height;

  const shrinkFactor = 0.75;
  const overshootFactor = 1.1;

  const minDuration = 150;
  const maxDuration = 325;
  const duration = clamp((Math.abs(deltaX) - 25) * 1.8, minDuration, maxDuration);
  // console.log(duration);
  endSpan.animate(
    [
      {
        transform: `translate(${deltaX}px, ${deltaY}px) scale(${scaleX}, ${scaleY})`,
        offset: 0,
      },
      {
        // Shrink a bit during the middle of the animation
        transform: `translate(${deltaX * 0.5}px, ${deltaY * 0.5}px) scale(${scaleX * shrinkFactor}, ${scaleY * shrinkFactor})`,
        offset: 0.4,
      },
      {
        // Slightly overshoot the final size
        transform: `scale(${overshootFactor}, ${overshootFactor})`,
        offset: 0.85,
      },
      {
        transform: 'none',
        offset: 1,
      },
    ],
    {
      duration,
      easing,
    }
  );
}
